import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  FolderKanban,
  Plus,
  Loader2,
  X,
  CheckSquare,
  Paperclip,
  BookOpen,
} from "lucide-react";
import {
  useProjects,
  useCreateProject,
  useMembers,
  type ProjectEstado,
} from "../api/projects";

const ESTADO_BADGE: Record<ProjectEstado, string> = {
  activo: "bg-emerald-50 text-emerald-700",
  pausado: "bg-amber-50 text-amber-700",
  completado: "bg-sky-50 text-sky-700",
  archivado: "bg-slate-100 text-slate-500",
};

export function ProyectosListPage() {
  const { data: projects, isLoading } = useProjects();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="p-8 h-full flex flex-col gap-6 min-h-0 overflow-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <FolderKanban size={28} className="text-brand-700" /> Proyectos
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona proyectos, sus archivos, historias de usuario y tareas.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 text-sm font-bold"
        >
          <Plus size={16} /> Nuevo proyecto
        </button>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      ) : !projects || projects.length === 0 ? (
        <div className="grid place-items-center py-20 text-center text-slate-400">
          <FolderKanban size={40} className="mb-3" />
          <p className="font-bold text-slate-600">Aún no hay proyectos.</p>
          <p className="text-sm">Crea el primero con «Nuevo proyecto».</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <Link
              key={p.id}
              to="/projects/$id"
              params={{ id: String(p.id) }}
              className="group rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0"
                  style={{ background: p.color || "#0b4a2f" }}
                >
                  <FolderKanban size={20} />
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ESTADO_BADGE[p.estado]}`}>
                  {p.estado}
                </span>
              </div>
              <h3 className="mt-3 font-black text-slate-900 group-hover:text-brand-700">{p.nombre}</h3>
              {p.descripcion && (
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{p.descripcion}</p>
              )}
              <div className="mt-4 flex items-center gap-4 text-xs font-semibold text-slate-500">
                <span className="inline-flex items-center gap-1"><CheckSquare size={13} /> {p.tareas_count ?? 0}</span>
                <span className="inline-flex items-center gap-1"><BookOpen size={13} /> {p.user_stories_count ?? 0}</span>
                <span className="inline-flex items-center gap-1"><Paperclip size={13} /> {p.archivos_count ?? 0}</span>
                {p.responsable && <span className="ml-auto truncate">{p.responsable.nombre}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateProjectModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateProjectModal({ onClose }: { onClose: () => void }) {
  const create = useCreateProject();
  const { data: members } = useMembers();
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    estado: "activo" as ProjectEstado,
    color: "#0b4a2f",
    responsable_id: "" as string,
    fecha_inicio: "",
    fecha_fin: "",
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    await create.mutateAsync({
      nombre: form.nombre.trim(),
      descripcion: form.descripcion || null,
      estado: form.estado,
      color: form.color,
      responsable_id: form.responsable_id ? Number(form.responsable_id) : null,
      fecha_inicio: form.fecha_inicio || null,
      fecha_fin: form.fecha_fin || null,
    });
    onClose();
  }

  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-slate-900">Nuevo proyecto</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</span>
          <input className={input} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} autoFocus />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Descripción</span>
          <textarea className={`${input} min-h-[70px]`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</span>
            <select className={input} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value as ProjectEstado })}>
              <option value="activo">Activo</option>
              <option value="pausado">Pausado</option>
              <option value="completado">Completado</option>
              <option value="archivado">Archivado</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Color</span>
            <input type="color" className="w-full h-[38px] rounded-lg border border-slate-300" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Responsable</span>
          <select className={input} value={form.responsable_id} onChange={(e) => setForm({ ...form, responsable_id: e.target.value })}>
            <option value="">Sin asignar</option>
            {members?.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Inicio</span>
            <input type="date" className={input} value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fin</span>
            <input type="date" className={input} value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={create.isPending || !form.nombre.trim()} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
            {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear
          </button>
        </div>
      </form>
    </div>
  );
}
